import { useMemo, useState } from 'react';
import { ChevronDown, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useCompanyContacts, useUpdateTicket, type Contact } from '@/api/hooks';

interface Props {
  ticketId: number;
  companyId: number | null;
  value: number | null;
  className?: string;
}

function contactName(c: Contact): string {
  return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
}

/**
 * Ticket contact picker. Shows the current contact's name with their email
 * beneath it, and lets you reassign to any active contact at the ticket's
 * company. Updates the ticket's contactID.
 */
export function ContactPicker({ ticketId, companyId, value, className }: Props) {
  const contacts = useCompanyContacts(companyId);
  const mutation = useUpdateTicket();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const items = contacts.data?.items ?? [];
  const current = items.find((c) => c.id === value);
  const currentLabel = current
    ? contactName(current)
    : value == null
    ? 'No contact'
    : `#${value}`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      contactName(c).toLowerCase().includes(q) || (c.emailAddress ?? '').toLowerCase().includes(q),
    );
  }, [items, query]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const select = (contactId: number | null, label: string) => {
    mutation.mutate(
      { id: ticketId, fields: { contactID: contactId } },
      {
        onSuccess: () => { toast.success(contactId == null ? 'Contact cleared' : `Contact → ${label}`); setOpen(false); },
        onError: (err) => toast.error('Failed to update contact', { description: err instanceof Error ? err.message : 'Unknown error' }),
      },
    );
  };

  return (
    <div className={cn('flex flex-col items-end', className)}>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger
          disabled={mutation.isPending || companyId == null}
          title={currentLabel}
          className={cn(
            'flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-sm',
            'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            mutation.isPending && 'opacity-60',
            value == null && 'text-muted-foreground',
          )}
        >
          {mutation.isPending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
          <span className="truncate">{currentLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72 overflow-hidden p-0">
          <div className="p-2">
            <Input
              autoFocus
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <DropdownMenuSeparator className="my-0" />
          <div className="max-h-72 overflow-y-auto overflow-x-hidden py-1">
            <DropdownMenuItem onSelect={() => select(null, 'No contact')} className="gap-2 text-muted-foreground">
              <UserX className="h-4 w-4 shrink-0" />
              <span className="truncate">No contact</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {contacts.isLoading && <DropdownMenuItem disabled>Loading contacts…</DropdownMenuItem>}
            {!contacts.isLoading && filtered.length === 0 && (
              <DropdownMenuItem disabled>{companyId == null ? 'No company on ticket' : 'No matches'}</DropdownMenuItem>
            )}
            {filtered.map((c) => {
              const name = contactName(c) || `#${c.id}`;
              return (
                <DropdownMenuItem
                  key={c.id}
                  onSelect={(e) => { e.preventDefault(); select(c.id, name); }}
                  className={cn('flex flex-col items-start gap-0.5', c.id === value && 'bg-accent text-accent-foreground')}
                >
                  <span className="truncate">{name}</span>
                  {c.emailAddress && (
                    <span className="truncate text-[11px] text-muted-foreground">{c.emailAddress}</span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {current?.emailAddress && (
        <a
          href={`mailto:${current.emailAddress}`}
          className="max-w-full truncate px-2 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
          title={current.emailAddress}
        >
          {current.emailAddress}
        </a>
      )}
    </div>
  );
}
